import React from "react";
import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  Editor,
  TLResizeInfo,
} from "@tldraw/tldraw";
import { getCloudPath, getDashArray, inkyCloudSvgPath } from "../new-geo-shape-helpers";
import { defaultScale, defaultSize } from "../constants";
import { HOVER_EFFECT } from "./constants";
import { ShapeUtilHelper } from "./ShapeUtilHelper";

type NewCloudUtilType = TLBaseShape<
  "customCloud",
  {
    w: number;
    h: number;
    fill: string;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColor: string;
    dash: "solid" | "dashed";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
  }
>;

export class CustomCloudUtil extends BaseBoxShapeUtil<NewCloudUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customCloud" as const;
  static override props: RecordProps<NewCloudUtilType> = {
    w: T.number,
    h: T.number,
    fill: T.string,
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColor: T.string,
    dash: T.any,
    transparency: T.number,
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any,
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    filterBlur: T.optional(T.number),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    scale: T.optional(T.number),
  };

  // Default properties for the shape
  getDefaultProps(): NewCloudUtilType["props"] {
    return {
      w: 100,
      h: 60,
      fill: "#e3e3e3",
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  // Set up shape bounds and dimensions
  getGeometry(shape: NewCloudUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }
  hideSelectionBoundsFg(_shape: NewCloudUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: NewCloudUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: NewCloudUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: NewCloudUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: NewCloudUtilType, info: TLResizeInfo<NewCloudUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  // Method to render the shape based on props
  component(shape: NewCloudUtilType) {
    const scale = defaultScale;
    const size = defaultSize;

    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = shape.props;

    const shadowFilterId = `shadow-${shape.id}`;
    const dashArray = strokeDasharray || getDashArray(dash);

    // Get the cloud path based on dash style
    const pathData =
      //@ts-ignore
      dash === "solid" ? getCloudPath(w, h, shape.id, size, scale) : inkyCloudSvgPath(w, h, shape.id, size, scale);
    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }

  addDefaultColorMix(shape: NewCloudUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: NewCloudUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: NewCloudUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  // Indicator function to display shape in editor
  indicator(shape: NewCloudUtilType) {
    const {
      h,
      w,
      scale,
      dash,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
    } = shape.props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const size = defaultSize;
    const pathData =
      //@ts-ignore
      dash === "solid" ? getCloudPath(w, h, shape.id, size, scale!) : inkyCloudSvgPath(w, h, shape.id, size, scale!);

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={pathData}
        transform={`translate(${isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0}, ${
          isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0
        })`}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}

/**
 * Utility function to mix colors with opacity
 */
export function mixColors(colors: string[], keywords: { [key: string]: string } = {}): string {
  if (colors.length === 0) return "#000000";

  const hexToRgba = (hex: string) => {
    let r = 0,
      g = 0,
      b = 0,
      a = 1;

    if (hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
    } else if (hex.length === 9) {
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
      a = parseInt(hex.slice(7, 9), 16) / 255;
    }

    return { r, g, b, a };
  };

  const rgbaToHex = (r: number, g: number, b: number, a: number) => {
    const alpha = Math.round(a * 255)
      .toString(16)
      .padStart(2, "0");
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}${alpha}`;
  };

  const resolveColor = (color: string) => {
    return keywords[color.toLowerCase()] || color;
  };

  const colorToHex = (color: string) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const data = ctx.getImageData(0, 0, 1, 1).data;
      return rgbaToHex(data[0], data[1], data[2], data[3] / 255);
    }
    return "#000000";
  };

  const avgColor = colors
    .map((color) => hexToRgba(resolveColor(colorToHex(color))))
    .reduce(
      (acc, color) => {
        acc.r += color.r * color.a;
        acc.g += color.g * color.a;
        acc.b += color.b * color.a;
        acc.a += color.a;
        return acc;
      },
      { r: 0, g: 0, b: 0, a: 0 }
    );

  const totalAlpha = colors
    .map((color) => hexToRgba(resolveColor(colorToHex(color))))
    .reduce((acc, color) => acc + color.a, 0);

  if (totalAlpha === 0) {
    return "#00000000"; // Return fully transparent color if all colors are fully transparent
  }

  const mixedColor = rgbaToHex(
    Math.round(avgColor.r / totalAlpha),
    Math.round(avgColor.g / totalAlpha),
    Math.round(avgColor.b / totalAlpha),
    totalAlpha / colors.length
  );

  return mixedColor;
}
